<template>
  <div class="w-[30rem]">
    <div class="">
      <div
        v-if="useHold"
        class="font-semibold py-3 pt-4 px-10 rounded-t-box bg-base-content/10"
      >
        On tap
      </div>

      <ActionSubmenu
        class="px-4 pt-3 pb-4"
        :keycodes="state.tapKeycodes"
        :keycodeOptions="tapKeycodeOptions"
        :actions="state.tapActions"
        @newKeycodes="(kc: KeyCode[]) => updateAction({ tapKeycodes: kc })"
        @newActions="(a: NonKeycodeAction[]) => updateAction({ tapActions: a })"
      />
    </div>

    <div class="">
      <div
        class="py-3 px-10 bg-base-content/10 flex justify-between items-center"
        :class="useHold ? 'font-semibold' : ''"
      >
        <span class="">
          {{ useHold ? "On hold" : "Enable Hold/Tap" }}
        </span>

        <input type="checkbox" class="toggle" v-model="useHold" />
      </div>

      <ActionSubmenu
        v-if="useHold"
        class="px-4 pt-3 pb-4"
        :keycodes="state.holdKeycodes"
        :keycodeOptions="holdKeycodeOptions"
        :actions="state.holdActions"
        @newKeycodes="(kc: KeyCode[]) => updateAction({ holdKeycodes: kc })"
        @newActions="(a: NonKeycodeAction[]) => updateAction({ holdActions: a })"
      />
    </div>

    <div v-if="useHold" class="" :class="useLocalHoldTapOpts ? '' : ''">
      <div
        class="py-3 px-10 bg-base-content/10 flex justify-between items-center"
        :class="useLocalHoldTapOpts ? 'font-semibold' : 'rounded-b-box'"
      >
        <span class="">Hold/Tap overrides</span>
        <input type="checkbox" class="toggle" v-model="useLocalHoldTapOpts" />
      </div>

      <div v-if="useLocalHoldTapOpts" class="px-4 pt-3 pb-4 pl-6">
        <HoldTapOptions
          v-model:timeout="holdTapOpts.timeout"
          v-model:tap_hold_interval="holdTapOpts.tap_hold_interval"
          v-model:config="holdTapOpts.config"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch, onUnmounted } from "vue";
import {
  KeyCode,
  ActionInternal,
  ActionInternalNested,
  isHoldTapInternal,
  isMultipleActionsInternal,
  HoldTapInternal,
  HoldTapSettings,
  isNoOp,
  isTrans,
  isKeyCode,
  isMultipleKeyCodes,
  isLayer,
  isDefaultLayer,
  isCustom,
  KEYCODES,
  NonKeycodeAction,
  flattenCustomAction,
  unflattenNonkeycodeAction,
} from "@/keyboard";
import { useActionMenuCacheStore, useKeyboardStore } from "@/stores";
import ActionSubmenu from "@/components/actions/ActionSubmenu.vue";
import HoldTapOptions from "@/components/actions/HoldTapOptions.vue";

const props = defineProps<{
  action: ActionInternal;
  cacheKey: string;
}>();
const emit = defineEmits<{
  (_e: "newAction", _action: ActionInternal): void;
}>();

const kb = useKeyboardStore();
const cache = useActionMenuCacheStore();

// TODO: less hacky way to avoid loosing local options
// Because this component gets unmounted via v-if we have to preserve state between
// mounts. State is cached by row-col key.
const data = cache.get(props.cacheKey);

const useHold = ref(
  data.useHold ||
    // Force useHold if holdtap action is on.
    (isHoldTapInternal(props.action) && !isNoOp(props.action.HoldTap.hold))
);

const initialSettings = isHoldTapInternal(props.action)
  ? props.action.HoldTap.settings
  : undefined;
const useLocalHoldTapOpts = ref(initialSettings != undefined);
const holdTapOpts = reactive<HoldTapSettings>({
  config: initialSettings?.config ?? kb.defaults.config,
  tap_hold_interval:
    initialSettings?.tap_hold_interval ?? kb.defaults.tap_hold_interval,
  timeout: initialSettings?.timeout ?? kb.defaults.timeout,
});
const holdActionCache = ref(data.holdActionCache);

onUnmounted(() => {
  // Do not cache holdTapOpts because this is unintuitive - if someone changes defaults
  // then they most likely expect that they get the new defaults.
  data.useHold = useHold.value;
  data.useLocal = useLocalHoldTapOpts.value;
  data.holdActionCache = holdActionCache.value;
});

const remainingKeycodes = (used: KeyCode[]): KeyCode[] =>
  KEYCODES.filter((kc) => !used.includes(kc));

// Representation of an action in the menu, splitting 'hold' and 'tap' keys/actions as in the submenus.
type Internal = {
  tapKeycodes: KeyCode[];
  holdKeycodes: KeyCode[];
  tapActions: NonKeycodeAction[];
  holdActions: NonKeycodeAction[];
};

const actionKeycodes = (act: ActionInternal): KeyCode[] => {
  if (
    isNoOp(act) ||
    isTrans(act) ||
    isLayer(act) ||
    isDefaultLayer(act) ||
    isCustom(act)
  )
    return [];
  else if (isKeyCode(act)) return [act.KeyCode];
  else if (isMultipleKeyCodes(act)) return act.MultipleKeyCodes;
  else if (isHoldTapInternal(act))
    throw Error("HoldTap supported only at root of action tree");
  else if (isMultipleActionsInternal(act))
    return act.MultipleActions.flatMap(actionKeycodes);
  throw Error("Unknown action type");
};

const actionNonKeycodes = (act: ActionInternal): NonKeycodeAction[] => {
  if (isNoOp(act) || isKeyCode(act) || isMultipleKeyCodes(act)) return [];
  else if (isTrans(act) || isLayer(act) || isDefaultLayer(act)) return [act];
  else if (isCustom(act)) return [flattenCustomAction(act)];
  else if (isHoldTapInternal(act))
    throw Error("HoldTap supported only at root of action tree");
  else if (isMultipleActionsInternal(act))
    return act.MultipleActions.flatMap(actionNonKeycodes);
  throw Error("Unknown action type");
};

const holdTapToInternal = (act: HoldTapInternal): Internal => {
  const [hold, tap] = [act.HoldTap.hold, act.HoldTap.tap];
  return {
    tapKeycodes: actionKeycodes(tap),
    holdKeycodes: actionKeycodes(hold),
    tapActions: actionNonKeycodes(tap),
    holdActions: actionNonKeycodes(hold),
  };
};

const actionToInternal = (act: ActionInternal): Internal =>
  isHoldTapInternal(act)
    ? holdTapToInternal(act)
    : {
        tapKeycodes: actionKeycodes(act),
        tapActions: actionNonKeycodes(act),
        holdKeycodes: [],
        holdActions: [],
      };

const internalToAction = (internal: Internal): ActionInternal => {
  const { tapKeycodes, tapActions, holdKeycodes, holdActions } = internal;

  if (useHold.value && holdKeycodes.length + holdActions.length > 0) {
    // Recurse, making sure that hold keycodes/actions are empty to avoid infinite recursion
    const tap = internalToAction({
      tapKeycodes,
      tapActions,
      holdKeycodes: [],
      holdActions: [],
    });
    const hold = internalToAction({
      tapKeycodes: holdKeycodes,
      tapActions: holdActions,
      holdKeycodes: [],
      holdActions: [],
    });
    if (isHoldTapInternal(tap) || isHoldTapInternal(hold))
      throw Error("Assertion failed: nested resolved to HoldTap");
    if (isNoOp(hold)) throw Error("Assertion failed: Hold resolved to NoOp");

    const settings = useLocalHoldTapOpts.value
      ? {
          config: holdTapOpts.config,
          timeout: holdTapOpts.timeout,
          tap_hold_interval: holdTapOpts.tap_hold_interval,
        }
      : undefined;
    return {
      HoldTap: {
        hold,
        tap,
        settings,
      },
    };
  }

  // Resolve non hold-tap action
  tapActions.forEach((a) => {
    // ignore unflattening custom actions as we only search for HoldTap
    if (isHoldTapInternal(a as ActionInternal))
      throw Error("HoldTap supported only at root of action tree");
  });

  const actions: ActionInternalNested[] = tapActions.map(
    unflattenNonkeycodeAction
  );
  if (tapKeycodes.length == 1) actions.push({ KeyCode: tapKeycodes[0] });
  else if (tapKeycodes.length > 1)
    actions.push({ MultipleKeyCodes: tapKeycodes });

  if (actions.length == 0) return "NoOp";
  else if (actions.length == 1) return actions[0];
  else return { MultipleActions: actions };
};

const state = computed(() => actionToInternal(props.action));

const tapKeycodeOptions = computed(() =>
  remainingKeycodes(state.value.tapKeycodes)
);
const holdKeycodeOptions = computed(() =>
  remainingKeycodes(state.value.holdKeycodes)
);

const updateAction = (update: Partial<Internal>) => {
  const newState = Object.assign({}, state.value, update);
  const act = internalToAction(newState);
  console.log(JSON.stringify(act, null, 2));
  emit("newAction", act);
};

watch(
  [useHold, holdTapOpts, useLocalHoldTapOpts],
  ([newUseHold], [oldUseHold]) => {
    // Manage cached hold action
    if (newUseHold != oldUseHold) {
      if (newUseHold) {
        // Load from cache
        if (holdActionCache.value != null) {
          const { tapKeycodes, tapActions } = actionToInternal(
            holdActionCache.value
          );
          state.value.holdKeycodes = tapKeycodes;
          state.value.holdActions = tapActions;
        }
      } else if (isHoldTapInternal(props.action)) {
        // Store in cache
        holdActionCache.value = JSON.parse(
          JSON.stringify(props.action.HoldTap.hold)
        );
      }
    }
    // Just trigger the call, which will re-evaluate internalToAction
    updateAction({});
  }
);
</script>
